/**
 * Decide dialog for a pending dual-control config-change request.
 *
 * Wires POST /api/agentic/config-approvals/{id}/decide
 * (backend/app/routes/agentic_config.py `decide`, permission
 * agentic:config:approve; service config_approvals.decide_config_change —
 * checker<>maker enforced by the service AND a DB CHECK, checker permission
 * re-checked in-service, 72h pending TTL enforced at decide time).
 *
 * Decisions offered:
 *   Approve      → {decision:"approve"}  — agent_configuration changes are
 *                  APPLIED in the same transaction; a safe_mode_exit request
 *                  becomes an APPROVED authorization that a separate,
 *                  audited exit action consumes (approving never lifts the
 *                  brake by itself).
 *   Reject       → {decision:"reject"}   — terminal, nothing applied.
 *   Decide later → client-side only. The backend has no "deferred" state
 *                  (status CHECK: pending|approved|rejected|applied|expired)
 *                  and a pending row simply stays pending until decided or
 *                  it expires — so defer sends NOTHING and writes nothing.
 *
 * Every refusal the server makes (403 self-approval / missing permission,
 * 409 already decided, 410 expired) is shown verbatim; on 409/410 the list is
 * refetched so the stale card updates.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Modal, Stack, Text } from '@smms/design-system';
import {
  decideConfigApproval,
  type ApprovalDecision,
  type ConfigApproval,
} from '../api/agentic';
import { ApiError } from '../api/client';
import { describeMutationError, type MutationErrorView } from './mutationError';

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export interface DecideApprovalDialogProps {
  approval: ConfigApproval | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DecideApprovalDialog({
  approval,
  open,
  onOpenChange,
}: DecideApprovalDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<MutationErrorView | null>(null);

  const mutation = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: ApprovalDecision }) =>
      decideConfigApproval(id, decision),
    onSuccess: async () => {
      setError(null);
      // Approvals list, overview "pending approvals" KPI, safe-mode state
      // (an approved exit authorization is now spendable) all move.
      await queryClient.invalidateQueries({ queryKey: ['agentic'] });
      onOpenChange(false);
    },
    onError: err => {
      setError(describeMutationError(err, 'agentic:config:approve'));
      if (err instanceof ApiError && (err.status === 409 || err.status === 410)) {
        void queryClient.invalidateQueries({ queryKey: ['agentic', 'config-approvals'] });
      }
    },
  });

  const pending = mutation.isPending;

  const close = (next: boolean) => {
    if (pending) return;
    if (!next) setError(null);
    onOpenChange(next);
  };

  if (!approval) return null;

  const isSafeModeExit = approval.change_type === 'safe_mode_exit';

  return (
    <Modal open={open} onOpenChange={close} size="md" disableBackdropClose={pending}>
      <Modal.Header
        title={`Review: ${humanize(approval.change_type)}${
          approval.agent_id ? ` · ${humanize(approval.agent_id)}` : ''
        }`}
        description={
          isSafeModeExit
            ? 'Approving authorizes a Safe-Mode exit; it does not lift the brake. The exit itself is a separate, audited action.'
            : 'Approving applies this change immediately (same transaction). Rejecting applies nothing.'
        }
      />
      <Modal.Body>
        <Stack gap="sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="warning" size="sm">
              {approval.status}
            </Badge>
            {approval.requires_dual_control && (
              <Badge variant="info" size="sm">
                dual-control
              </Badge>
            )}
          </div>
          <Text variant="caption" tone="muted">
            Proposed {formatWhen(approval.proposed_at)} by {approval.maker_user_id}. You cannot
            decide a request you proposed yourself.
          </Text>
          <pre
            className="overflow-x-auto rounded-md bg-elevated p-3 text-xs text-text"
            // Same scrollable-region pattern as DecisionDetailPage's JsonBlock.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            aria-label="Proposed change (raw JSON)"
          >
            {JSON.stringify(approval.proposed_change, null, 2)}
          </pre>
          {error && (
            <div role="alert" className="rounded-md border border-danger/40 bg-danger/10 p-3">
              <p className="text-sm text-danger">{error.message}</p>
              {error.hint && <p className="mt-1 text-xs text-danger">{error.hint}</p>}
            </div>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => close(false)} disabled={pending}>
          Decide later
        </Button>
        <Button
          variant="destructive"
          onClick={() => mutation.mutate({ id: approval.id, decision: 'reject' })}
          disabled={pending}
          isLoading={pending && mutation.variables?.decision === 'reject'}
          loadingLabel="Rejecting"
        >
          Reject
        </Button>
        <Button
          variant="primary"
          onClick={() => mutation.mutate({ id: approval.id, decision: 'approve' })}
          disabled={pending}
          isLoading={pending && mutation.variables?.decision === 'approve'}
          loadingLabel="Approving"
        >
          Approve
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
